import { useAutoBuildHierarchy } from "@/hooks/mutations/use-auto-build";
import { useIsReflecting } from "@/components/table-preview/table/use-editable-preview";
import { cn } from "@/lib/utils";
import { usePreviewStore } from "@/stores/preview";
import { Button } from "@/styles/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/styles/ui/tabs";
import { ChevronDown, Loader, RotateCw, Wand2 } from "lucide-react";

export type SidebarTabMenu = "LINE_ITEM" | "AXIS";

export const PreviewDimensionTabs = () => {
  const { dimension, setDimension } = usePreviewStore((state) => state);

  return (
    <div className="group-data-[collapsible=icon]:hidden flex justify-center items-center h-[28px]">
      <Tabs value={dimension} className="flex justify-center items-center ">
        <TabsList className="flex justify-start items-center gap-2 bg-transparent p-0">
          <PreviewDimensionButton
            sidebarTabMenu={"LINE_ITEM"}
            setDimension={setDimension}
          />
          <PreviewDimensionButton
            sidebarTabMenu={"AXIS"}
            setDimension={setDimension}
          />
        </TabsList>
      </Tabs>
    </div>
  );
};
export const ReflectingIndicator = () => {
  const isReflecting = useIsReflecting();
  if (!isReflecting) return null;

  return (
    <div className="flex h-[32px] items-center gap-1 px-[12px] text-neutral-500">
      <Loader className="size-3 text-neutral-500 animate-spin" />
      <span className="caption-1-medium">반영중</span>
    </div>
  );
};

export const AutoBuildButton = () => {
  const { mutate, isPending, ready } = useAutoBuildHierarchy();
  const disabled = isPending || !ready;

  // 버튼 클릭 시 메뉴가 열리고, 두 모드 중 하나를 선택해 실행한다.
  // 순서를 다시 바꾸려면 버튼을 다시 클릭해 모드를 고르면 된다.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="flex h-[32px] pl-[12px] pr-[10px] py-[8px] items-center justify-center gap-1 shadow-none border rounded-md border-[#E5E5E5] bg-white text-black hover:bg-neutral-50"
          disabled={disabled}
          aria-label="순서 자동 지정 방식 선택"
        >
          {isPending ? (
            <RotateCw className="w-4 h-4 animate-spin" />
          ) : (
            <Wand2 className="w-4 h-4" />
          )}
          <span className="caption-1-medium">순서 자동 지정</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[--radix-dropdown-menu-trigger-width] w-[--radix-dropdown-menu-trigger-width]"
      >
        <AutoBuildModeItem label="구조 기반" onClick={() => mutate(false)} />
        <AutoBuildModeItem label="순서만" onClick={() => mutate(true)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AutoBuildModeItem = (props: { label: string; onClick: () => void }) => (
  <DropdownMenuItem
    className="caption-1-medium cursor-pointer"
    onClick={props.onClick}
  >
    {props.label}
  </DropdownMenuItem>
);

const PreviewDimensionButton = (props: {
  sidebarTabMenu: SidebarTabMenu;
  setDimension: (dimension: SidebarTabMenu) => void;
}) => {
  const { sidebarTabMenu, setDimension } = props;

  const value = {
    LINE_ITEM: "행",
    AXIS: "열",
    TEXTBLOCK: "문장",
  };

  return (
    <TabsTrigger
      value={sidebarTabMenu}
      className={cn(
        "flex items-center justify-center",
        "h-7 px-3 py-2",
        "rounded-[8px]",
        "text-[12px] font-medium tracking-[0.3024px]",
        "transition-all duration-200 ease-in-out",
        "bg-transparent text-neutral-500",
        "hover:bg-neutral-50",
        "focus:outline-none",
        "data-[state=active]:bg-neutral-100",
        "data-[state=active]:text-primary",
        "data-[state=active]:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
      )}
      onClick={() => {
        setDimension(sidebarTabMenu as SidebarTabMenu);
      }}
    >
      <span className="select-none leading-[1.334]">
        {value[sidebarTabMenu]}
      </span>
    </TabsTrigger>
  );
};
